import {useFormik} from 'formik'
import {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import * as Yup from 'yup'
import 'react-quill/dist/quill.snow.css'
import {TFormSignature, TFormTabSignature} from '../signatureFormsTypes'
import {INIT_FORM_SIGNATURE, tabOrder} from '../signatureFormsConst'
import {SignatureFormSettings} from './componentsModalAdd/SignatureFormSettings'
import {SignatureFormNotifications} from './componentsModalAdd/SignatureFormNotifications'
import {
  CREATE_SIGNATURE_FORM,
  UPDATE_SIGNATURE_FORM,
} from '../../../gql/mutations/signatureFormsMutations'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {SignatureFormContent} from './componentsModalAdd/SignatureFormContent'
import {NodeSignatureForm} from '../../../types/signatureFormsApi'
import {parseId} from '../../../helpers'

interface IModalProps {
  keyboard: boolean
  backdrop: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  idFormEdit?: NodeSignatureForm | null
}

export const ModalAddSignatureForm: React.FC<IModalProps> = ({
  keyboard,
  backdrop,
  isOpen,
  onClose,
  refetch,
  idFormEdit,
}) => {
  const [createSignatureForm, {data: dataCreate}] = useMutation(CREATE_SIGNATURE_FORM)

  const [updateSignatureForm, {data: dataUpdate}] = useMutation(UPDATE_SIGNATURE_FORM)

  const FormsTabs: {id: TFormTabSignature; num: number; name: string; icon?: string}[] = [
    {id: 'form-settings', num: 1, name: 'Form settings', icon: 'bi-person'},
    {id: 'form-notifications', num: 2, name: 'Form notifications', icon: 'bi-people'},
    {id: 'form-content', num: 3, name: 'Form content', icon: 'bi-file-earmark-text'},
    // {id: 'form-disclaimers', num: 4, name: 'Form disclaimers', icon: 'bi-credit-card'},
  ]

  const [formTab, setFormTab] = useState<TFormTabSignature>('form-settings')
  const [prevTab, setPrevTab] = useState<TFormTabSignature | null>(null)
  const [nextTab, setNextTab] = useState<TFormTabSignature | null>(null)

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too Short!').max(250, 'Too Long!').required('(Required)'),
    formContent: Yup.string().required('(Required)'),
  })

  const formik = useFormik<TFormSignature>({
    validationSchema: validationSchema,
    initialValues: {...INIT_FORM_SIGNATURE},
    onSubmit: (v) => {
      const input = {
        name: v.name,
        description: v.descripcion,
        formContent: v.formContent,
        emailContent: v.emailContent,
        smsContent: v.smsContent,
        status: v.status,
        language: v.language,
      }
      if (idFormEdit && Number(parseId(idFormEdit.id) ?? 0) > 0) {
        updateSignatureForm({
          variables: {
            id: parseId(idFormEdit.id),
            input,
          },
        })
      } else {
        createSignatureForm({variables: input})
      }
    },
  })

  const isEditMode = !!(idFormEdit && Number(parseId(idFormEdit.id) ?? 0) > 0)

  const handleSaveWithoutContent = () => {
    const v = formik.values
    updateSignatureForm({
      variables: {
        id: parseId(idFormEdit!.id),
        input: {
          name: v.name,
          description: v.descripcion,
          emailContent: v.emailContent,
          smsContent: v.smsContent,
          status: v.status,
          language: v.language,
        },
      },
    })
  }

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldValue(field, value)
    // console.log('Cambiando campo:', field, value)
  }

  const handleFieldBlur = (field: string) => {
    formik.setFieldTouched(field, true)
  }

  const getPrevTab = (currentTab: TFormTabSignature): TFormTabSignature | null => {
    const index = tabOrder.indexOf(currentTab)
    if (index > 0) {
      return tabOrder[index - 1]
    }
    return null // ya está en el primero
  }

  const getNextTab = (currentTab: TFormTabSignature): TFormTabSignature | null => {
    const index = tabOrder.indexOf(currentTab)
    if (index >= 0 && index < tabOrder.length - 1) {
      return tabOrder[index + 1]
    }
    return null // ya está en el último tab
  }
  const handleNextTab = async () => {
    const nextTab = getNextTab(formTab)
    if (nextTab) setFormTab(nextTab)
  }
  const handlePrevTab = () => {
    const prevTab = getPrevTab(formTab)
    if (prevTab) setFormTab(prevTab)
  }

  useEffect(() => {
    if (!formTab) return
    console.log(formTab)
    setPrevTab(getPrevTab(formTab))
    setNextTab(getNextTab(formTab))
  }, [formTab])

  useEffect(() => {
    if (!dataCreate) return
    if (dataCreate.signatureFormCreate?.success) {
      toast.success('Signature Form added successfully')
      refetch()
      onClose()
    } else if (dataCreate.signatureFormCreate?.errors?.length > 0) {
      toast.error(
        `Error: ${dataCreate.signatureFormCreate?.errors[0]?.message || 'Something wrong'}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (!dataUpdate) return
    if (dataUpdate.signatureFormUpdate?.success) {
      toast.success('Signature Form Updated successfully')
      refetch()
      onClose()
    } else if (dataUpdate.signatureFormUpdate?.errors?.length > 0) {
      toast.error(
        `Error: ${dataUpdate.signatureFormUpdate?.errors[0]?.message || 'Something wrong'}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  useEffect(() => {
    if (!idFormEdit) return
    formik.setValues({
      name: idFormEdit.name ?? '',
      descripcion: idFormEdit.description ?? '',
      emailContent: idFormEdit.emailContent ?? '',
      smsContent: idFormEdit.smsContent ?? '',
      formContent: idFormEdit.formContent ?? '',
      status: idFormEdit.status ?? 'ACTIVE',
      language: idFormEdit.language ?? 'es',
      id: idFormEdit.id ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFormEdit])

  return (
    <Modal
      backdrop='static'
      keyboard={false}
      show={isOpen}
      onHide={onClose}
      dialogClassName='modal-wide'
    >
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-x-2'>
          {isEditMode ? 'Edit signature form' : 'Create a signature form'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <div className='signature-form-tabs'>
            {FormsTabs.map((ft) => (
              <div
                onClick={() => {
                  setFormTab(ft.id)
                }}
                key={ft.num}
                className={ft.id === formTab ? 'tab active' : 'tab'}
                style={{flex: 1}}
              >
                <button key={ft.num} className='btn btn-tabs' disabled={ft.id === formTab}>
                  <span className='policy-form-tabs-number'>{ft.num}</span>
                  <span style={{color: ft.name === formTab ? '#5d78ff' : 'gray'}}>{ft.name}</span>
                </button>
              </div>
            ))}
          </div>
          <div className='signature-form-tabs-container'>
            {formTab === 'form-settings' && (
              <SignatureFormSettings
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleChange={handleFieldChange}
                handleBlur={handleFieldBlur}
              />
            )}
            {formTab === 'form-notifications' && (
              <SignatureFormNotifications
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleChange={handleFieldChange}
                handleBlur={handleFieldBlur}
              />
            )}
            {formTab === 'form-content' && (
              <SignatureFormContent
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleChange={handleFieldChange}
                handleBlur={handleFieldBlur}
              />
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className='btn-prev-next'>
          {prevTab ? (
            <Button size='sm' variant='outline' onClick={handlePrevTab}>
              PREVIOUS
            </Button>
          ) : (
            <div style={{width: '100px'}}></div> // espacio fijo para mantener estructura
          )}
          <div>
            {!prevTab && (
              <Button size='sm' className='me-3' variant='outline' onClick={onClose}>
                CLOSE
              </Button>
            )}{' '}
            {nextTab ? (
              <Button size='sm' onClick={handleNextTab}>
                NEXT STEP
              </Button>
            ) : isEditMode ? (
              <>
                <Button size='sm' variant='secondary' className='me-2' onClick={handleSaveWithoutContent}>
                  Save
                </Button>
                <Button size='sm' variant='info' onClick={formik.submitForm}>
                  Save All
                </Button>
              </>
            ) : (
              <Button size='sm' variant='info' onClick={formik.submitForm}>
                Save
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
